/* eslint-disable jsx-a11y/label-has-associated-control */

import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import React, { useEffect, useState } from "react";

import {
    Box,
    Card,
    Grid,
    Container,
    CardContent,
    CardActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
} from "@mui/material";
import { clientGrey } from "../../theme/style";

import useForm from "../../hooks/validation";
import { BOARD_TITLE_OPTIONS } from "./constants";
import { styles } from "./style";
import Iconify from "../../components/iconify/iconify";
import LoadingButton from "../../shared/loadingButton/LoadingButton";
import { placeholderStye } from "../../theme/style";
import MuiTextField from "../../theme/TextField";
import boardServices from "../../services/boardServices";
import useFetchData from "../../hooks/fetchData";
import ImageWithFallback from "../../components/ImageWithFallback";
import { getErrorMessage } from "../../utils";



const validationRules = {
    name: (value) => (value ? "" : "This field is required"),
    title: (value) => (value ? "" : "This field is required"),
};
const EditBoard = () => {
    const navigate = useNavigate();

    const { id } = useParams();
    const { data: board, loading: boardLoading } = useFetchData(boardServices.getByID, id);
    
    const initialValues = {
        name: (!boardLoading && board?.name) || '',
        title: (!boardLoading && board?.title) || '',
        description: (!boardLoading && board?.description) || '',
    };

    const { values, errors, handleOnChange, validateAllFields, setFormValues } =
        useForm(initialValues, validationRules);


    const [memberImage, setMemberImage] = useState((!boardLoading && board?.image) || '');


    useEffect(() => {
        setFormValues({
            name: board?.name ?? '',
            title: board?.title ?? '',
            description: board?.description ?? '',
        });
        setMemberImage((!boardLoading && board?.image) || '');
    }, [board]);

    const [isLoading, setIsLoading] = useState(false);

    const handleFileChange = (e) => {
        const file = e.target.files[0];

        const fileSize = file.size / (1024 * 1024);
        if (fileSize > 3) {
            toast.error("File size exceeds the maximum limit of 3 MB.");
            return;
        }
        setMemberImage(file);
    };

    const handleSubmit = async () => {
        if (!validateAllFields()) return;
        const selectedOption = BOARD_TITLE_OPTIONS.find((opt) => opt.value === values.title);
        const formData = new FormData();
        formData.append("name", values.name);
        formData.append("title", values.title);
        formData.append("order", String(selectedOption ? selectedOption.order : board?.order ?? 0));
        if (values.description) formData.append("description", values.description);
        if (typeof memberImage === "object" && memberImage) formData.append("image", memberImage);
        setIsLoading(true);
        boardServices
                .edit(formData, id)
                .then(() => {
                    toast.success("Board updated successfully")
                    navigate(-1)
                    setIsLoading(false)
                })
                .catch((err) => {
                    toast.error(getErrorMessage(err))
                    setIsLoading(false)
                });
    };

    return (
        <Container>
            <Card sx={{ padding: "1rem" }}>
                <CardContent>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Box sx={{ ...styles.mainWrapper, ...(!memberImage && { maxWidth: "100%", width: "100%" }) }}>
                                <input
                                    type="file"
                                    name="staffProfileImage"
                                    id="staff_profile_img"
                                    accept="image/*"
                                    style={{ display: "none" }}
                                    onChange={handleFileChange}
                                />
                                {memberImage ? (
                                    <Box sx={styles.imagePreviewBlock}>
                                        <Box sx={styles.imageWrapper}>
                                            <Box sx={{ width: "100%", height: "100%" }}>
                                                {typeof memberImage === "object" && memberImage instanceof File ? (
                                                    <img
                                                        height="100%"
                                                        width="100%"
                                                        style={{ objectFit: "cover" }}
                                                        src={URL.createObjectURL(memberImage)}
                                                        alt="Board member"
                                                    />
                                                ) : (
                                                    <ImageWithFallback
                                                        path={memberImage}
                                                        alt="Board member"
                                                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                    />
                                                )}
                                            </Box>
                                        </Box>
                                        <label htmlFor="staff_profile_img">
                                            <Typography
                                                component="span"
                                                variant="caption"
                                                sx={styles.changeImageLink}
                                            >
                                                <Iconify icon="eva:edit-2-outline" sx={{ width: 14, height: 14, mr: 0.5, verticalAlign: "middle" }} />
                                                Change image
                                            </Typography>
                                        </label>
                                    </Box>
                                ) : (
                                    <label
                                        htmlFor="staff_profile_img"
                                        style={{ cursor: "pointer", display: "block" }}
                                    >
                                        <Box sx={[styles.imageWrapper, styles.imageWrapperEmpty]}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: "#495057",
                                                    textAlign: "center",
                                                    lineHeight: 1.3,
                                                }}
                                            >
                                                Click here
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: "#495057",
                                                    textAlign: "center",
                                                    mt: 0.5,
                                                }}
                                            >
                                                to add image
                                            </Typography>
                                        </Box>
                                    </label>
                                )}
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <MuiTextField
                                label="name"
                                name="name"
                                value={values.name}
                                error={errors.name}
                                helperText={errors.name}
                                onChange={handleOnChange}
                                inputProps={placeholderStye}
                                placeholder="Name"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography pb="4px" fontWeight={500} fontSize={14} color={clientGrey[500]}>
                                Title
                            </Typography>
                            <FormControl fullWidth size="medium" error={!!errors.title}>
                                <InputLabel id="board-edit-title-label">Select title</InputLabel>
                                <Select
                                    labelId="board-edit-title-label"
                                    id="board-edit-title-select"
                                    value={values.title}
                                    label="Select title"
                                    onChange={(e) => handleOnChange({ target: { name: "title", value: e.target.value } })}
                                    sx={{ backgroundColor: clientGrey[100] }}
                                >
                                    {BOARD_TITLE_OPTIONS.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.title && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                                        {errors.title}
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <MuiTextField
                                label="Description"
                                name="description"
                                value={values.description}
                                error={errors.description}
                                helperText={errors.description}
                                onChange={handleOnChange}
                                placeholder="Brief bio or description (optional)"
                                multiline
                                minRows={4}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
                <CardActions>
                    <LoadingButton
                        isLoading={isLoading}
                        variant="contained"
                        onClick={handleSubmit}
                    >
                        Update Board Member
                    </LoadingButton>
                </CardActions>
            </Card>
        </Container>
    );
};

export default EditBoard;
